import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { usePedidoMovement } from '@/hooks/usePedidoMovement';
import { depoisDoClique, justificarLinhas } from '@/components/kanban/triagem/acaoTriagem';

export const COLUNA_COMPLEMENTAR = 'PEDIDO COMPLEMENTAR';
const QUADRO_PRE_ATIVO: string = 'PRE_ATIVO';
/** Mesma régua das filas que travam (`fn_registrar_justificativa_fila`). */
export const MINIMO_COMPLEMENTAR = 15;

/** Quem entra no Complementar pelo botão acabou de abrir o chamado: são esses
 *  o motivo e a ação da justificativa da fila de lá (`fila_justificativa_opcoes`). */
const MOTIVO_ENTRADA = 'prd_sem_resposta';
const ACAO_ENTRADA = 'abri_prd';

export interface AlvoComplementar {
  pedidoId: string;
  numeroPedido: string;
  razaoSocial: string;
  cotacao: string | null;
  colunaOrigem: string;
  /** Nome da triagem de onde sai, para o histórico. */
  origem: string;
  /** A linha clicada primeiro; as outras da mesma cotação e do mesmo pedido vão junto. */
  linhas: Array<{ itemId: string; rotulo: string }>;
}

/**
 * PEDIDO COMPLEMENTAR, UMA JANELA PARA AS DUAS TRIAGENS (Douglas 29/10/2026,
 * Opção B): a Thuane trata o conflito de linha, abre o chamado na Claro e
 * manda a cotação para a Triagem Pedido Complementar. Vale na SMS Enviado e na
 * Nota Fiscal / Bilhete, com o mesmo código.
 *
 * O pedido anda inteiro (`fn_mover_pedido_junto`): as linhas da cotação vão
 * numa transação, ou nenhuma. O chamado e o texto viram a justificativa da
 * fila do Complementar, que é o que quem pega a linha lá enxerga.
 */
export function usePedidoComplementar() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { moverPedidoJunto } = usePedidoMovement();

  const enviar = useMutation({
    mutationFn: async ({ alvo, chamado, texto }: { alvo: AlvoComplementar; chamado: string; texto: string }) => {
      if (!chamado.trim()) throw new Error('Informe o número do chamado.');
      if (texto.trim().length < MINIMO_COMPLEMENTAR) throw new Error('Escreva o que o complementar precisa fazer.');

      const r = await moverPedidoJunto({
        pedidoId: alvo.pedidoId,
        itensIds: alvo.linhas.map(l => l.itemId),
        quadroOrigem: QUADRO_PRE_ATIVO,
        colunaOrigem: alvo.colunaOrigem,
        toQuadro: QUADRO_PRE_ATIVO as Parameters<typeof moverPedidoJunto>[0]['toQuadro'],
        toColuna: COLUNA_COMPLEMENTAR,
        motivo: `${alvo.origem} — conflito de linha, chamado ${chamado.trim()}`,
        origemTriagem: true,
      });

      const movidosIds = (r.movidos ?? []).map(m => m.item_id);
      const semJustificativa = await justificarLinhas({
        pedidoId: alvo.pedidoId, itens: movidosIds,
        justificativa: `Chamado ${chamado.trim()} · ${texto.trim()}`, motivo: MOTIVO_ENTRADA, acao: ACAO_ENTRADA,
      }).then(() => 0, () => movidosIds.length);
      return { pedidoId: alvo.pedidoId, itens: alvo.linhas.map(l => l.itemId), movidos: r.total ?? movidosIds.length, semJustificativa };
    },
    onSuccess: ({ pedidoId, itens, movidos, semJustificativa }) => {
      toast(semJustificativa > 0
        ? {
          title: 'Foi para o Pedido Complementar',
          description: 'O chamado não ficou gravado na fila de lá. Justifique pela Triagem Pedido Complementar.',
          variant: 'destructive',
        }
        : {
          title: 'Pedido Complementar',
          description: `${movidos > 1 ? `${movidos} linhas da mesma cotação foram` : 'A linha foi'} para o Complementar com o chamado.`,
        });
      void depoisDoClique(queryClient, ['triagem-portabilidade', 'triagem-nota-fiscal', 'triagem-ativacao', 'triagem-fluxo',
        'portabilidade-extras'], { pedidoId, itens }, 'sai');
    },
    onError: (error) => {
      toast({ title: 'Não foi para o Complementar', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    },
  });

  return { enviarAsync: enviar.mutateAsync, isEnviando: enviar.isPending };
}

/** As linhas que vão juntas: a mesma cotação, no mesmo pedido, ainda na fila. */
export function linhasDaCotacao<T extends { item_id: string; pedido_id: string; numero_cotacao_operadora: string | null }>(
  itens: T[], clicado: T, rotulo: (i: T) => string,
): AlvoComplementar['linhas'] {
  const cotacao = clicado.numero_cotacao_operadora;
  const juntas = cotacao
    ? itens.filter(i => i.pedido_id === clicado.pedido_id && i.numero_cotacao_operadora === cotacao)
    : [clicado];
  return [clicado, ...juntas.filter(i => i.item_id !== clicado.item_id)]
    .map(i => ({ itemId: i.item_id, rotulo: rotulo(i) }));
}
